import axios from '@/api/axios.config'
import { lotusTurnoverWorstCaseMs } from '@messenger/types'
import type { LotusTurnoverBodyInput } from '@messenger/types'
import type { EditorPageContentBundle } from '@/utils/editor-text-box/editorTextBoxAdapter'

/**
 * Room for what the route does after its transaction commits: one Lotus
 * bundle read per page of the set, then the answer.
 */
const LOTUS_TURNOVER_AFTER_COMMIT_MS = 30_000

/**
 * How long the turnover call waits: the server's worst case for its
 * transaction (every attempt waiting its longest for a connection, then
 * running to its timeout) plus the reads after the commit. A call that
 * expires here is one the server could not have answered in time, so its
 * result is unknown, not lost: the transaction committed whole or not at all.
 */
const LOTUS_TURNOVER_TIMEOUT_MS =
  lotusTurnoverWorstCaseMs() + LOTUS_TURNOVER_AFTER_COMMIT_MS

type LotusTurnoverResult = {
  orderPageSetId: string
  runId: string
  /** Every page written, lowest id first, with its bundle as the editor's page content load answers it; null for a page removed since the commit. */
  pages: Array<{ pageId: string; bundle: EditorPageContentBundle | null }>
}

/**
 * Writes a whole page set's turnover in one server transaction: each page's
 * JSON, its converted boxes, the drafts pending in the editor (each with the
 * `expectedRevision` it was edited from) and the pending deletes, then points
 * the line at the set. The body must name the version taken before anything
 * was converted; a page that version no longer holds is refused (409,
 * `code: 'CONFLICT'`), and so is a page whose stored run id is not the
 * `loadedRunId` it names (409, `code: 'order_page_turned_over'`); either
 * refusal writes nothing.
 */
export const turnOverPageSetToLotus = async (
  orderPageSetId: string,
  body: LotusTurnoverBodyInput,
) =>
  await axios.post<LotusTurnoverResult>(
    `orderPageSets/${orderPageSetId}/lotus-turnover`,
    body,
    { timeout: LOTUS_TURNOVER_TIMEOUT_MS },
  )
